/**
 * Portfolio Dashboard Content
 *
 * Displays the user's portfolio summary and strategy allocations.
 * Shows empty state when no wallet connected or no allocations.
 */

import clsx from "clsx";
import { Link } from "react-router";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";

import { usePortfolio } from "@/hooks/usePortfolio";
import { useAllocations } from "@/hooks/useAllocations";
import type { StrategyAllocation } from "@/types/api.types";
import { formatCurrency, formatDate } from "@/utils/format";

// ============================================================================
// Main Component
// ============================================================================

export default function Portfolio() {
  const { portfolio, isLoading: portfolioLoading } = usePortfolio();
  const {
    allocations,
    isLoading: allocationsLoading,
    deallocate,
    isDeallocating,
  } = useAllocations();
  const [withdrawTarget, setWithdrawTarget] = useState<StrategyAllocation | null>(null);

  // Loading state
  if (portfolioLoading || allocationsLoading) {
    return <PortfolioSkeleton />;
  }

  // Derive values from portfolio data (defaults to 0 when no portfolio yet)
  const totalAllocated = portfolio?.totalAllocated ?? 0;
  const totalCurrentValue =
    (portfolio?.totalCurrentValue ?? 0) + (portfolio?.availableBalance ?? 0);
  const totalUnrealizedPnl = portfolio?.totalUnrealizedPnl ?? 0;
  const totalUnrealizedPnlPercent = portfolio?.totalUnrealizedPnlPercent ?? 0;
  const totalRealizedPnl = portfolio?.totalRealizedPnl ?? 0;
  const hasAllocations = allocations.length > 0;

  return (
    <div className="space-y-8">
      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <SummaryCard label="Total Allocated" value={formatCurrency(totalAllocated, 0)} />
        <SummaryCard label="Current Value" value={formatCurrency(totalCurrentValue, 0)} />
        <SummaryCard
          label="Unrealized P&L"
          value={`${totalUnrealizedPnl >= 0 ? "+" : ""}${formatCurrency(totalUnrealizedPnl, 0)}`}
          subValue={`${totalUnrealizedPnlPercent >= 0 ? "+" : ""}${totalUnrealizedPnlPercent.toFixed(2)}%`}
          valueClassName={totalUnrealizedPnl >= 0 ? "text-emerald-400" : "text-rose-400"}
        />
        <SummaryCard
          label="Realized P&L"
          value={`${totalRealizedPnl >= 0 ? "+" : ""}${formatCurrency(totalRealizedPnl, 0)}`}
          valueClassName={totalRealizedPnl >= 0 ? "text-emerald-400" : "text-rose-400"}
        />
      </div>

      {/* Available Balance */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400">Available Balance</p>
            <p className="text-2xl font-semibold text-white">
              {portfolio?.availableBalance != null
                ? formatCurrency(portfolio.availableBalance, 0)
                : "—"}
            </p>
          </div>
          <Link
            to="/strategies"
            className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
          >
            {hasAllocations ? "Allocate More" : "Allocate to Strategies"}
          </Link>
        </div>
      </div>

      {/* Allocations List or Empty CTA */}
      {hasAllocations ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Your Allocations</h2>
            <span className="text-sm text-slate-400">
              {allocations.length} {allocations.length === 1 ? "strategy" : "strategies"}
            </span>
          </div>

          <div className="grid gap-4">
            {allocations.map((allocation) => (
              <AllocationCard
                key={allocation.id}
                allocation={allocation}
                onWithdraw={() => setWithdrawTarget(allocation)}
                isWithdrawing={isDeallocating}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-700 py-12">
          <h2 className="text-lg font-semibold text-slate-300">No allocations yet</h2>
          <p className="mt-2 text-sm text-slate-500">
            Allocate capital to trading strategies to start earning returns
          </p>
          <div className="mt-4 flex gap-3">
            <Link
              to="/portfolio/build"
              className="rounded-full bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600"
            >
              Build My Portfolio
            </Link>
            <Link
              to="/strategies"
              className="rounded-full border border-slate-700 px-6 py-2.5 text-sm font-semibold text-slate-300 transition hover:bg-slate-800"
            >
              Browse Strategies
            </Link>
          </div>
        </div>
      )}

      {withdrawTarget && (
        <WithdrawModal
          allocation={withdrawTarget}
          isWithdrawing={isDeallocating}
          onConfirm={async (amount) => {
            await deallocate({
              allocationId: withdrawTarget.id,
              amount,
            });
            setWithdrawTarget(null);
          }}
          onClose={() => setWithdrawTarget(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sub-Components
// ============================================================================

interface SummaryCardProps {
  label: string;
  value: string;
  subValue?: string;
  valueClassName?: string;
}

function SummaryCard({ label, value, subValue, valueClassName = "" }: SummaryCardProps) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
      <p className="text-sm text-slate-400">{label}</p>
      <p className={clsx("mt-1 text-xl font-semibold", valueClassName || "text-white")}>
        {value}
      </p>
      {subValue && <p className={clsx("text-sm", valueClassName)}>{subValue}</p>}
    </div>
  );
}

interface AllocationCardProps {
  allocation: StrategyAllocation;
  onWithdraw: () => void;
  isWithdrawing: boolean;
}

function AllocationCard({ allocation, onWithdraw, isWithdrawing }: AllocationCardProps) {
  const allocatedDate = formatDate(allocation.allocatedAt);

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Strategy Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-white">{allocation.strategyName}</h3>
            <span
              className={clsx(
                "rounded px-2 py-0.5 text-xs font-medium",
                allocation.status === "active"
                  ? "bg-emerald-500/20 text-emerald-400"
                  : allocation.status === "withdrawing"
                    ? "bg-amber-500/20 text-amber-400"
                    : "bg-slate-500/20 text-slate-400"
              )}
            >
              {allocation.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-400">
            Allocated on {allocatedDate} | Venue: {allocation.venue}
          </p>
        </div>

        {/* Metrics */}
        <div className="flex gap-6">
          <div className="text-right">
            <p className="text-xs text-slate-500">Allocated</p>
            <p className="font-semibold text-white">
              {formatCurrency(allocation.allocatedAmount, 0)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Current Value</p>
            <p className="font-semibold text-white">
              {formatCurrency(allocation.currentValue, 0)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Unrealized P&L</p>
            <p
              className={clsx(
                "font-semibold",
                allocation.unrealizedPnl >= 0 ? "text-emerald-400" : "text-rose-400"
              )}
            >
              {allocation.unrealizedPnl >= 0 ? "+" : ""}
              {formatCurrency(allocation.unrealizedPnl)}
              <span className="ml-1 text-xs">
                ({allocation.unrealizedPnlPercent >= 0 ? "+" : ""}
                {allocation.unrealizedPnlPercent.toFixed(2)}%)
              </span>
            </p>
          </div>
        </div>

        {/* Actions */}
        {allocation.status === "active" && (
          <button
            onClick={onWithdraw}
            disabled={isWithdrawing}
            className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 transition hover:border-rose-500 hover:text-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isWithdrawing ? "Withdrawing..." : "Withdraw"}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Withdraw Modal
// ============================================================================

interface WithdrawModalProps {
  allocation: StrategyAllocation;
  isWithdrawing: boolean;
  onConfirm: (amount: number) => Promise<void>;
  onClose: () => void;
}

function WithdrawModal({
  allocation,
  isWithdrawing,
  onConfirm,
  onClose,
}: WithdrawModalProps) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);

  const maxAmount = Number(allocation.currentValue);
  const parsedAmount = parseFloat(amount);
  const isValid = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= maxAmount;

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    setError(null);
    try {
      await onConfirm(parsedAmount);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Withdrawal failed");
    }
  }, [isValid, parsedAmount, onConfirm]);

  const setPreset = (pct: number) => {
    const raw = maxAmount * pct;
    // Floor to 2 decimal places to avoid rounding above maxAmount
    const clamped = Math.min(Math.floor(raw * 100) / 100, maxAmount);
    setAmount(clamped.toFixed(2));
    setError(null);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-slate-800 p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">
            Withdraw from {allocation.strategyName}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 transition hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 rounded-lg bg-slate-700/50 p-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Current Value</span>
            <span className="font-semibold text-white">{formatCurrency(maxAmount)}</span>
          </div>
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-slate-400">Allocated</span>
            <span className="text-slate-300">
              {formatCurrency(Number(allocation.allocatedAmount))}
            </span>
          </div>
        </div>

        <label className="mb-1 block text-sm font-medium text-slate-300">
          Withdrawal Amount
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            $
          </span>
          <input
            type="number"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError(null);
            }}
            placeholder="0.00"
            min={0}
            max={maxAmount}
            step="0.01"
            className="w-full rounded-lg border border-slate-600 bg-slate-700 py-2.5 pl-7 pr-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            autoFocus
          />
        </div>

        <div className="mt-2 flex gap-2">
          {[0.25, 0.5, 0.75, 1].map((pct) => (
            <button
              key={pct}
              onClick={() => setPreset(pct)}
              className="flex-1 rounded-md border border-slate-600 py-1 text-xs font-medium text-slate-300 transition hover:border-slate-400 hover:text-white"
            >
              {pct === 1 ? "Max" : `${pct * 100}%`}
            </button>
          ))}
        </div>

        {error && <p className="mt-2 text-sm text-rose-400">{error}</p>}

        {amount && !isValid && (
          <p className="mt-2 text-sm text-amber-400">
            {parsedAmount > maxAmount
              ? `Amount exceeds current value ($${maxAmount.toFixed(2)})`
              : "Enter a valid amount greater than $0"}
          </p>
        )}

        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            disabled={isWithdrawing}
            className="flex-1 rounded-lg border border-slate-600 py-2.5 text-sm font-semibold text-slate-300 transition hover:border-slate-400 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || isWithdrawing}
            className="flex-1 rounded-lg bg-rose-500 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isWithdrawing
              ? "Withdrawing..."
              : `Withdraw $${isValid ? parsedAmount.toFixed(2) : "0.00"}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PortfolioSkeleton() {
  return (
    <div className="animate-pulse space-y-8">
      {/* Summary Cards Skeleton */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-xl border border-slate-700 bg-slate-800 p-4">
            <div className="h-4 w-24 rounded bg-slate-700" />
            <div className="mt-2 h-7 w-32 rounded bg-slate-700" />
          </div>
        ))}
      </div>

      {/* Balance Section Skeleton */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-4 w-28 rounded bg-slate-700" />
            <div className="mt-2 h-8 w-36 rounded bg-slate-700" />
          </div>
          <div className="h-10 w-28 rounded-lg bg-slate-700" />
        </div>
      </div>

      {/* Allocations List Skeleton */}
      <div className="space-y-4">
        <div className="h-6 w-36 rounded bg-slate-700" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-slate-700 bg-slate-800 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-5 w-32 rounded bg-slate-700" />
                <div className="mt-2 h-4 w-48 rounded bg-slate-700" />
              </div>
              <div className="flex gap-6">
                <div className="h-12 w-20 rounded bg-slate-700" />
                <div className="h-12 w-20 rounded bg-slate-700" />
                <div className="h-12 w-24 rounded bg-slate-700" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
